import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { customToast } from '../utils/customToast';
import { supportTicketService } from '../services/supportTicketService';
import { Search, Plus, MessageSquare, ChevronRight, ChevronLeft, Eye, Unlock, Clock, CheckCircle2, Lock, RefreshCw } from 'lucide-react';
import { SupportTicketModal } from '../components/SupportTicketModal';

interface TicketReply {
  id: number;
  message: string;
  is_admin: boolean;
  created_at: string;
}

interface SupportTicket {
  id: number;
  subject: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  created_at: string;
  updated_at: string;
  replies?: TicketReply[];
}

const MyTicketsPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'open' | 'in-progress' | 'resolved' | 'closed'>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
    loadTickets();
  }, [filterStatus, searchTerm]);

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const status = filterStatus === 'all' ? undefined : filterStatus === 'in-progress' ? 'in-progress' : filterStatus;
      const response = await supportTicketService.getMyTickets({ 
        status,
        search: searchTerm,
        page: currentPage 
      });
      setTickets(response.data);
      setTotalPages(response.pagination.last_page);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      customToast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, currentPage, searchTerm]);

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.id.toString().includes(searchTerm) ||
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return theme === 'dark' ? 'bg-red-900/20 text-red-400 border-red-500/30' : 'bg-red-50 text-red-700 border-red-200';
      case 'high':
        return theme === 'dark' ? 'bg-orange-900/20 text-orange-400 border-orange-500/30' : 'bg-orange-50 text-orange-700 border-orange-200';
      case 'medium':
        return theme === 'dark' ? 'bg-yellow-900/20 text-yellow-400 border-yellow-500/30' : 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:
        return theme === 'dark' ? 'bg-blue-900/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-700 border-blue-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return theme === 'dark' ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-50 text-blue-700';
      case 'in-progress':
        return theme === 'dark' ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-50 text-purple-700';
      case 'resolved':
        return theme === 'dark' ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-700';
      case 'closed':
        return theme === 'dark' ? 'bg-gray-900/20 text-gray-400' : 'bg-gray-50 text-gray-700';
      default:
        return '';
    }
  };

  return (
    <>
      <SupportTicketModal 
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          loadTickets();
        }}
      />
      
      <div className="space-y-3 sm:space-y-4 lg:space-y-6">
        {/* Header */}
        <div className="flex flex-col space-y-3 sm:space-y-4">
          <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                My Support Tickets
              </h1>
              <p className={`mt-1 text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                View and manage your support requests
              </p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => loadTickets()}
                disabled={loading}
                className={`flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl font-medium touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed ${
                  theme === 'dark'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
                title="Refresh Tickets"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl font-medium touch-manipulation"
                title="Create New Ticket"
              >
                <Plus size={16} />
                <span className="text-sm">New Ticket</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className={`p-4 rounded-lg border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-9 pr-4 py-2 text-sm rounded-lg border transition-colors ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                } focus:outline-none`}
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-sm font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Status:
              </span>
              {['all', 'open', 'in-progress', 'resolved', 'closed'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status as any)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    filterStatus === status
                      ? 'bg-blue-600 text-white'
                      : theme === 'dark'
                        ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {status === 'in-progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tickets - Mobile Card View */}
        <div className="sm:hidden space-y-3">
          {loading ? (
            [...Array(4)].map((_, index) => (
              <div key={index} className={`p-4 rounded-lg border animate-pulse ${
                theme === 'dark'
                  ? 'bg-slate-800 border-slate-700'
                  : 'bg-white border-gray-200'
              }`}>
                <div className={`h-6 w-24 rounded mb-2 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'}`} />
                <div className={`h-4 w-32 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'}`} />
              </div>
            ))
          ) : filteredTickets.length === 0 ? (
            <div className={`p-8 text-center rounded-lg border ${
              theme === 'dark'
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-gray-200'
            }`}>
              <MessageSquare className={`w-8 h-8 mx-auto mb-2 opacity-30 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`} />
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                No support tickets found
              </p>
            </div>
          ) : (
            filteredTickets.map(ticket => (
              <div key={ticket.id} className={`p-4 rounded-lg border transition-colors ${
                theme === 'dark'
                  ? 'bg-slate-800 border-slate-700 hover:border-slate-600'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <p className={`font-mono text-xs mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Ticket #{ticket.id}
                    </p>
                    <h3 className={`font-semibold text-sm truncate ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {ticket.subject}
                    </h3>
                  </div>
                  <button
                    onClick={() => navigate(`/dashboard/my-tickets/${ticket.id}`)}
                    className={`ml-2 flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${
                      theme === 'dark'
                        ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border-blue-500/30'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200'
                    }`}
                  >
                    <Eye size={12} />
                    View
                  </button>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs gap-2">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Priority</span>
                    <span className={`px-2 py-0.5 rounded text-xs border ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs gap-2">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Status</span>
                    <span className={`px-2 py-0.5 rounded text-xs border inline-flex items-center gap-1 ${getStatusColor(ticket.status)}`}>
                      {ticket.status === 'open' && <Unlock size={12} />}
                      {ticket.status === 'in-progress' && <Clock size={12} />}
                      {ticket.status === 'resolved' && <CheckCircle2 size={12} />}
                      {ticket.status === 'closed' && <Lock size={12} />}
                      {ticket.status === 'in-progress' ? 'In Progress' : ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Replies</span>
                    <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>{ticket.replies?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Created</span>
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>{new Date(ticket.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Tickets - Desktop Table View */}
        <div className={`hidden sm:block rounded-lg border overflow-hidden ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          {/* Table Header */}
          <div className={`border-b px-6 py-3 ${
            theme === 'dark' ? 'bg-slate-700/50 border-slate-700' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="grid grid-cols-7 gap-4 text-sm font-semibold">
              <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Ticket</div>
              <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Subject</div>
              <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Priority</div>
              <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Status</div>
              <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Replies</div>
              <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Created</div>
              <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Action</div>
            </div>
          </div>

          {/* Table Body */}
          {loading ? (
            <div className="divide-y" style={{
              borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
            }}>
              {[...Array(8)].map((_, index) => (
                <div
                  key={index}
                  className={`px-6 py-4 animate-pulse ${
                    theme === 'dark'
                      ? 'hover:bg-slate-700/50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="grid grid-cols-7 gap-4 items-center text-sm">
                    {/* Ticket ID skeleton */}
                    <div className={`h-4 w-12 rounded ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                    {/* Subject skeleton */}
                    <div className={`h-4 w-32 rounded ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                    {/* Priority skeleton */}
                    <div className={`h-6 w-16 rounded ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                    {/* Status skeleton */}
                    <div className={`h-6 w-20 rounded ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                    {/* Replies skeleton */}
                    <div className={`h-4 w-8 rounded ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                    {/* Date skeleton */}
                    <div className={`h-4 w-20 rounded ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                    {/* Button skeleton */}
                    <div className={`h-8 w-16 rounded-lg ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className={`px-6 py-12 text-center`}>
              <MessageSquare className={`w-12 h-12 mx-auto mb-3 opacity-30 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`} />
              <p className={`text-sm font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                No support tickets found
              </p>
            </div>
          ) : (
            <div className="divide-y" style={{
              borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
            }}>
              {filteredTickets.map(ticket => (
                <div
                  key={ticket.id}
                  className={`px-6 py-4 transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-slate-700/50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="grid grid-cols-7 gap-4 items-center text-sm">
                    <div>
                      <p className={`font-mono text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        #{ticket.id}
                      </p>
                    </div>
                    <div className={`truncate ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                    }`}>
                      {ticket.subject}
                    </div>
                    <div>
                      <span className={`px-2 py-1 rounded text-xs border ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                      </span>
                    </div>
                    <div>
                      <span className={`px-2 py-1 rounded text-xs border inline-flex items-center gap-1 ${getStatusColor(ticket.status)}`}>
                        {ticket.status === 'open' && <Unlock size={14} />}
                        {ticket.status === 'in-progress' && <Clock size={14} />}
                        {ticket.status === 'resolved' && <CheckCircle2 size={14} />}
                        {ticket.status === 'closed' && <Lock size={14} />}
                        {ticket.status === 'in-progress' ? 'In Progress' : ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                      </span>
                    </div>
                    <div className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {ticket.replies?.length || 0}
                    </div>
                    <div className={`text-xs ${
                      theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </div>
                    <div>
                      <button
                        onClick={() => navigate(`/dashboard/my-tickets/${ticket.id}`)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${
                          theme === 'dark'
                            ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border-blue-500/30'
                            : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200'
                        }`}
                      >
                        <Eye size={12} />
                        View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={`border-t px-6 py-3 flex items-center justify-between ${
              theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
            }`}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`flex items-center gap-1 px-3 py-1 rounded transition-colors text-sm ${
                  currentPage === 1
                    ? theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                    : theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-1 px-3 py-1 rounded transition-colors text-sm ${
                  currentPage === totalPages
                    ? theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                    : theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                }`}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MyTicketsPage;
